import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Mail, Printer, AlertTriangle, Sun, Briefcase, Cloud, Moon, Droplets, CalendarClock, RefreshCw, FileText, ShoppingBag, Activity } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, DISCLAIMER } from "../lib/api";
import { KeySeq, WaveLine } from "../components/site/Wave";
import { useCart } from "../context/CartContext";
import { SessionCalendar } from "../components/plan/SessionCalendar";

const TIMES = {
  morning: { label: "Morning", Icon: Sun, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  during_work: { label: "During work", Icon: Briefcase, cls: "bg-sky-50 text-sky-800 border-sky-200" },
  afternoon: { label: "Afternoon", Icon: Cloud, cls: "bg-stone-100 text-stone-700 border-stone-200" },
  evening: { label: "Evening", Icon: Moon, cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
};
const LENGTH = { one_day: "One-Time", one_week: "1-Week", thirty_day: "30-Day" };
const GETTING_STARTED = [
  ["Connect power", "Plug the power cord into the back of the machine, then into a wall outlet."],
  ["Choose your accessory", "Stainless Steel Cylinders (larger black cord, banana plugs in the right and left side of the machine) for full-body sessions, OR Electrode Pads (thin black cord plugs in the back of the machine like the power cord) — place pads on the left/right or front/back of the target area."],
  ["Enter your code", "Press AUTO, type the Code number, then press RUN. The session starts automatically. To change the minutes per frequency, enter the minutes first (e.g. 5, AUTO, Code, RUN)."],
  ["Pause & resume", "Press any number key (1–9) to pause. Press RUN to resume right where you left off."],
];

const Session = ({ s, i }) => {
  const t = TIMES[s.time_of_day] || TIMES.morning;
  return (
    <div data-testid={`session-card-${i}`} className="rounded-xl border border-[#E4DFD4] bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${t.cls}`}><t.Icon className="w-3.5 h-3.5" />{t.label}</span>
        <span className="font-mono text-sm font-bold text-[#0F766E]">{s.duration_min ? `${s.duration_min} min` : ""}</span>
      </div>
      <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <div className="shrink-0"><KeySeq keypad={s.keypad} code={s.code} /></div>
        <div className="min-w-0">
          <div className="font-semibold">{s.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {[s.minutes_per_frequency && `${s.minutes_per_frequency} min per frequency`, s.accessory && (s.accessory === "pads" ? "Electrode pads" : "Cylinders"), s.frequency].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>
      {s.notes && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{s.notes}</p>}
    </div>
  );
};

const Generating = () => (
  <div className="max-w-xl mx-auto text-center py-24" data-testid="plan-generating">
    <div className="relative h-24 rounded-2xl bg-[#0B1013] overflow-hidden"><WaveLine className="absolute inset-0" color="#06B6D4" /></div>
    <h1 className="font-display text-3xl mt-8">Building your program</h1>
    <p className="text-slate-600 mt-3">We're matching your answers and health documents to the Wave Therapy code catalog. This usually takes 30–90 seconds. A copy will also land in your inbox the moment it's ready.</p>
  </div>
);

const Notes = ({ plan }) => {
  const [notes, setNotes] = useState(plan.notes || "");
  const [saved, setSaved] = useState(true);
  const t = useRef();
  const change = (v) => {
    setNotes(v); setSaved(false);
    clearTimeout(t.current);
    t.current = setTimeout(() => api.put(`/plans/${plan.id}/notes`, { notes: v }).then(() => setSaved(true)).catch(() => {}), 900);
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E4DFD4] p-6 no-print">
      <div className="flex items-center justify-between"><h3 className="font-display text-xl">My notes & personal log</h3><span data-testid="plan-notes-status" className="text-xs text-slate-500">{saved ? "Saved" : "Saving…"}</span></div>
      <p className="text-xs text-slate-500 mt-1">Track how you feel, log session times, or note questions for your Wave Therapy wellness coach.</p>
      <textarea data-testid="plan-notes-input" className="field min-h-[120px]" value={notes} onChange={(e) => change(e.target.value)} placeholder={"Day 1 — ran 646 at 9pm, slept like a rock.\nQuestion for my wellness coach: …"} />
    </div>
  );
};

const Upsell = ({ ownsDevice }) => {
  const { add } = useCart();
  const [device, setDevice] = useState(null);
  const [coach, setCoach] = useState(null);
  useEffect(() => {
    api.get("/products/at-home-wave-therapy").then((r) => setDevice(r.data)).catch(() => {});
    api.get("/products/coaching-initial").then((r) => setCoach(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-4 no-print">
      {ownsDevice !== "yes" && device && (
        <div data-testid="plan-device-upsell" className="rounded-2xl bg-[#0B1013] text-white p-6 relative overflow-hidden">
          <WaveLine className="absolute inset-x-0 bottom-0 h-16" color="#06B6D4" opacity={0.4} />
          <div className="relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#06B6D4]">Run this plan at home</div>
            <h3 className="font-display text-xl mt-2">{device.name}</h3>
            <p className="text-sm text-slate-400 mt-2">Every code in your plan is pre-programmed. Includes free AI planning and a 3-year warranty.</p>
            <button data-testid="ai-plan-device-upsell-btn" onClick={() => add(device)} className="mt-4 rounded-full bg-[#06B6D4] text-[#0B1013] px-5 py-2.5 text-sm font-bold hover:bg-white transition-colors">Add device — ${device.price.toLocaleString()}</button>
          </div>
        </div>
      )}
      {coach && (
        <div className="rounded-2xl bg-white border border-[#E4DFD4] p-6">
          <h3 className="font-display text-lg">Want to talk through your plan?</h3>
          <p className="text-sm text-slate-600 mt-1">Book a virtual session with a Wave Therapy wellness coach.</p>
          <button data-testid="plan-coaching-add-btn" onClick={() => add(coach)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#0D9488] text-[#0D9488] px-5 py-2.5 text-sm font-semibold hover:bg-[#0D9488] hover:text-white transition-colors"><ShoppingBag className="w-4 h-4" /> Book coaching — ${coach.price}</button>
        </div>
      )}
    </div>
  );
};

export default function PlanPage() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [missing, setMissing] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    let timer, stop = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/plans/${id}`);
        if (stop) return;
        setPlan(data);
        if (data.status === "generating") timer = setTimeout(load, 4000);
      } catch { setMissing(true); }
    };
    load();
    return () => { stop = true; clearTimeout(timer); };
  }, [id]);

  const emailPlan = async () => {
    setEmailing(true);
    try { const { data } = await api.post(`/plans/${id}/email`); toast.success(`Plan sent to ${data.email}`); } catch (e) { toast.error(formatError(e)); } finally { setEmailing(false); }
  };
  const retry = async () => { await api.post(`/plans/${id}/retry`); setPlan({ ...plan, status: "generating" }); setTimeout(() => window.location.reload(), 4000); };

  if (missing) return <div className="max-w-xl mx-auto py-24 text-center"><p>We couldn't find that plan.</p><Link to="/questionnaire" className="text-[#0D9488] font-semibold">Start a new questionnaire</Link></div>;
  if (!plan || plan.status === "generating") return <Generating />;
  if (plan.status === "error") return (
    <div className="max-w-xl mx-auto py-24 text-center" data-testid="plan-error">
      <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
      <h1 className="font-display text-3xl mt-4">We hit a snag</h1>
      <p className="text-slate-600 mt-2">Your answers are saved. Please try generating again.</p>
      <button data-testid="plan-retry-btn" onClick={retry} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0D9488] text-white px-6 py-3 font-semibold"><RefreshCw className="w-4 h-4" /> Try again</button>
    </div>
  );

  const p = plan.plan || {};
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="plan-page">
      <img src="/logo-black.png" alt="CuraWaves" className="hidden print:block h-6 w-auto mb-6" />
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="max-w-3xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#0D9488]">CuraWaves Wave Therapy · {LENGTH[plan.program_length] || ""} program</span>
          <h1 data-testid="plan-title" className="font-display text-4xl sm:text-5xl font-semibold mt-3 leading-tight">{p.title}</h1>
          <p data-testid="plan-meta" className="mt-3 text-sm text-slate-500">Prepared for <b className="text-slate-700">{plan.first_name}</b> · Plan ID {plan.id.slice(0, 8).toUpperCase()} · Created {new Date(plan.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>
          <p data-testid="plan-summary" className="mt-5 text-slate-700 leading-relaxed">{p.summary}</p>
          {p.protocol_focus && <p data-testid="plan-focus" className="mt-3 text-sm text-slate-600"><b className="text-[#0F766E]">Focus:</b> {p.protocol_focus}</p>}
        </div>
        <div className="flex gap-2 no-print">
          <button data-testid="ai-plan-email-btn" disabled={emailing} onClick={emailPlan} className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors"><Mail className="w-4 h-4" />{emailing ? "Sending…" : "Email me this plan"}</button>
          <button data-testid="ai-plan-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full border border-[#E4DFD4] bg-white px-5 py-2.5 text-sm font-semibold hover:border-[#0D9488] transition-colors"><Printer className="w-4 h-4" />Print</button>
        </div>
      </div>

      {plan.safety_flag && <div data-testid="plan-safety-flag" className="mt-8 rounded-xl border border-red-300 bg-red-50 text-red-800 p-4 text-sm flex gap-2"><AlertTriangle className="w-5 h-5 shrink-0" />You indicated pregnancy or a pacemaker / implanted electronic device. Do not use Wave Therapy until you have discussed it with your physician.</div>}

      {p.daily_tips?.[0] && <div data-testid="plan-daily-tip" className="mt-8 rounded-xl bg-[#0D9488]/10 border border-[#0D9488]/30 px-5 py-4 text-sm text-slate-800"><b className="text-[#0F766E]">Daily tip:</b> {p.daily_tips[0]}</div>}

      <SessionCalendar plan={plan} />

      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {p.biowell_insights?.length > 0 && (
            <section data-testid="plan-biowell-insights" className="rounded-2xl bg-[#0B1013] text-white p-6 sm:p-8 relative overflow-hidden">
              <WaveLine className="absolute inset-x-0 top-0 h-16" color="#06B6D4" opacity={0.25} />
              <div className="relative">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#06B6D4]">Bio-Well insights</span>
                <h2 className="font-display text-2xl mt-2 flex items-center gap-2"><Activity className="w-6 h-6 text-[#06B6D4]" />What your energy scan shows</h2>
                {p.biowell_summary && <p data-testid="plan-biowell-summary" className="text-sm text-slate-300 mt-3 leading-relaxed">{p.biowell_summary}</p>}
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {p.biowell_insights.map((b, i) => (
                    <div key={i} data-testid={`biowell-reading-${i}`} className="rounded-xl bg-white/5 border border-white/10 p-4">
                      <div className="flex items-baseline justify-between gap-3"><span className="font-semibold">{b.reading}</span>{b.value && <span className="font-mono text-sm text-[#06B6D4]">{b.value}</span>}</div>
                      <p className="text-sm text-slate-300 mt-2">{b.meaning}</p>
                      {b.plan_link && <p className="text-xs text-[#06B6D4] mt-2">In your plan: {b.plan_link}</p>}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-5">Bio-Well readings are wellness and energy indicators only — not a medical diagnosis.</p>
              </div>
            </section>
          )}
          {(p.blocks || []).map((b, bi) => (
            <section key={bi} data-testid={`plan-block-${bi}`} className="rounded-2xl bg-[#EFECE5]/60 border border-[#E4DFD4] p-5 sm:p-6">
              <div className="flex flex-wrap items-baseline gap-3 mb-4"><h2 className="font-display text-2xl">{b.label}</h2><span className="text-sm text-slate-600">{b.focus}</span></div>
              <div className="space-y-3">{(b.sessions || []).map((s, si) => <Session key={si} s={s} i={`${bi}-${si}`} />)}</div>
            </section>
          ))}
        </div>
        <aside className="lg:col-span-4 space-y-5">
          <div className="rounded-2xl bg-white border border-[#E4DFD4] p-6">
            <h3 className="font-display text-lg flex items-center gap-2"><Droplets className="w-5 h-5 text-[#0D9488]" />How to run</h3>
            <ul className="text-sm text-slate-700 mt-3 space-y-2">
              <li>Drink 8 oz of water before and after every session.</li>
              <li>Press the keys in order shown. Default is 3 min per frequency — a leading number changes it.</li>
              <li>Pause with any number key, resume with RUN.</li>
              <li>Turn intensity down before starting; raise slowly to a gentle tingle.</li>
            </ul>
          </div>
          {p.safety_notes?.length > 0 && (
            <div className="rounded-2xl bg-[#FFFBEB] border border-amber-200 p-6" data-testid="plan-safety-notes">
              <h3 className="font-display text-lg text-amber-900">Safety notes</h3>
              <ul className="text-sm text-amber-900 mt-3 space-y-2 list-disc pl-4">{p.safety_notes.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {p.lab_insights?.length > 0 && (
            <div className="rounded-2xl bg-white border border-[#E4DFD4] p-6" data-testid="plan-lab-insights">
              <h3 className="font-display text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-[#0D9488]" />From your health data</h3>
              <ul className="text-sm text-slate-700 mt-3 space-y-2 list-disc pl-4">{p.lab_insights.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {p.daily_tips?.length > 0 && (
            <div className="rounded-2xl bg-white border border-[#E4DFD4] p-6" data-testid="plan-tips">
              <h3 className="font-display text-lg">Tips for the best results</h3>
              <ul className="text-sm text-slate-700 mt-3 space-y-2 list-disc pl-4">{p.daily_tips.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {p.freedom_tips?.length > 0 && (
            <div className="rounded-2xl bg-white border border-[#E4DFD4] p-6">
              <h3 className="font-display text-lg">F.R.E.E.D.O.M. coaching</h3>
              <div className="mt-3 space-y-3">{p.freedom_tips.map((f, i) => <div key={i} className="flex gap-3 text-sm"><span className="font-display text-2xl text-[#0D9488] w-6">{f.letter}</span><span className="text-slate-700">{f.tip}</span></div>)}</div>
            </div>
          )}
          {(plan.autoimmune || plan.program_length === "thirty_day") && (
            <div className="rounded-2xl bg-[#0D9488]/10 border border-[#0D9488]/30 p-6 no-print" data-testid="plan-reassess-card">
              <h3 className="font-display text-lg flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#0D9488]" />30-day re-assessment</h3>
              <p className="text-sm text-slate-700 mt-2">We'll email you a reminder in {p.reassess_in_days || 30} days to update your symptoms and refresh your program.</p>
              <Link to={`/questionnaire?from=${plan.id}`} data-testid="plan-reassess-link" className="text-sm font-semibold text-[#0D9488] mt-3 inline-block">Re-assess now →</Link>
            </div>
          )}
          <Upsell ownsDevice={plan.owns_device} />
        </aside>
      </div>
      <section data-testid="plan-getting-started" className="mt-10 rounded-2xl bg-white border border-[#E4DFD4] p-6 sm:p-8">
        <h2 className="font-display text-2xl">Getting started with your device</h2>
        <ol className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GETTING_STARTED.map(([title, body], i) => (
            <li key={title} className="rounded-xl bg-[#F8F7F4] border border-[#E4DFD4] p-4">
              <span className="font-mono text-xs text-[#0D9488]">0{i + 1}</span>
              <div className="font-semibold mt-1">{title}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
            </li>
          ))}
        </ol>
      </section>
      <div className="mt-10"><Notes plan={plan} /></div>
      <p className="mt-8 text-xs text-amber-800 bg-[#FFFBEB] border border-amber-200 rounded-lg p-4">{DISCLAIMER} This plan is generated by AI from the Wave Therapy Practitioner's Guide for educational purposes and is not medical advice. Consult your healthcare provider before starting any program.</p>
    </div>
  );
}
